// src/pages/OnlineOrdersPage.jsx
import React, { useState, useEffect } from 'react';
import { ShoppingBag } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { ordersManageApi, storeApi } from '../api';
import OrderFilterTabs from '../components/orders/OrderFilterTabs';
import OrderCard from '../components/orders/OrderCard';
import AssignDeliveryDialog from '../components/orders/AssignDeliveryDialog';
import ConfirmRefundDialog from '../components/orders/ConfirmRefundDialog';
import PrintLabelDocument from '../components/orders/PrintLabelDocument';

const OnlineOrdersPage = () => {
  const { currentUser, addWsListener } = useApp();
  const isHQ = currentUser?.store_type === 'headquarters';
  const isManager = currentUser?.role === 'manager';

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  // 当前选中tab的 value（第一项，对应 order_status）
  const [filterStatus, setFilterStatus] = useState('');
  // "已退款"这种特殊tab需要额外带的参数（比如 payment_status: 'refunded'），
  // 普通tab没有这个东西，是 null
  const [filterExtraParams, setFilterExtraParams] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const [showAssign, setShowAssign] = useState(false);
  const [assignOrderId, setAssignOrderId] = useState(null);
  const [assignOrderNo, setAssignOrderNo] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [assigning, setAssigning] = useState(false);

  // 打印:整个页面只保留一份打印目标数据，避免多张卡片各自渲染导致重叠
  const [printOrder, setPrintOrder] = useState(null);

  // 确认退款弹窗：需要填退款金额/方式/备注，不再是一句话 window.confirm 就直接调接口
  const [refundOrder, setRefundOrder] = useState(null);
  const [refundSubmitting, setRefundSubmitting] = useState(false);

  // tab 结构：[order_status值, 显示文案, 额外筛选参数(可选), 额外参数存在时的显示文案(可选)]
  // "已退款"和普通"已取消"在 order_status 上其实是同一个值（cancelled），
  // 只有 payment_status 才能把两者区分开，所以这个tab要多传一个 payment_status 参数，
  // 并且单独给一个"已退款"的显示文案（不然会和"已取消"那个tab重名）
  const filterTabs = isHQ
    ? [
        ['', '全部'],
        ['pending', '待指派'],
        ['assigned', '待接单'],
        ['confirmed', '已接单'],
        ['preparing', '备货中'],
        ['delivering', '配送中'],
        ['ready', '待自提'],
        ['completed', '已完成'],
        ['refunding', '待退款'],
        ['cancelled', '已取消'],
        ['cancelled', '已退款', { payment_status: 'refunded' }]
      ]
    : [
        ['', '全部'],
        ['assigned', '待接单'],
        ['confirmed', '已接单'],
        ['preparing', '备货中'],
        ['delivering', '配送中'],
        ['ready', '待自提'],
        ['completed', '已完成']
      ];

  useEffect(() => {
    loadOrders();
    if (isHQ) loadBranches();
    // isHQ 之前没在依赖数组里——如果 currentUser 是异步加载的（页面首次渲染那一刻
    // 还没拿到，isHQ 当时判定为false），等 currentUser 真正加载完成、isHQ 变成true，
    // 这个effect不会因为isHQ的变化而重新跑，loadBranches()就永远不会被补上，
    // 分店下拉框里会一直只剩"总店自行配送"这一个写死的选项，这正是之前反馈的bug
  }, [filterStatus, filterExtraParams, isHQ]);

  // 新订单来了：刷新列表（提示音已经挪到 AppContext.jsx 里全局播放，
  // 不管当前在哪个页面都会响，这里不再重复播放，不然这个页面开着的时候会响两次）。
  // 其他订单状态变化：同样只刷新列表。
  // 这个订阅跟着 filterStatus/filterExtraParams 一起重新绑定——不然回调函数会一直
  // "记住"组件第一次挂载那一刻的筛选条件，之后切换筛选标签，新订单来了刷新用的
  // 还是最初那份旧的筛选条件，不是你当前实际选中的标签
  useEffect(() => {
    const unsubscribe = addWsListener((msg) => {
      if (msg.type === 'NEW_ORDER' || msg.type === 'ORDER_UPDATED') {
        loadOrders();
      }
    });
    return unsubscribe;
  }, [addWsListener, filterStatus, filterExtraParams]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.order_status = filterStatus;
      if (filterExtraParams) Object.assign(params, filterExtraParams);
      const res = await ordersManageApi.getList(params);
      if (res.success) setOrders(res.data);
    } catch (err) {
      console.error('加载订单失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const res = await storeApi.getList();
      if (res.success) setBranches(res.data.filter(s => s.type === 'branch' && s.status === 'active'));
    } catch (err) { console.error(err); }
  };

  const toggleDetail = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const openAssign = (order) => {
    setAssignOrderId(order.id);
    setAssignOrderNo(order.order_no);
    setSelectedBranch('');
    setShowAssign(true);
  };

  const handleAssign = async () => {
    if (!selectedBranch) { alert('请选择配送方式'); return; }
    setAssigning(true);
    try {
      const isHQDelivery = selectedBranch === 'hq';
      const res = await ordersManageApi.assign(assignOrderId, {
        assigned_store_id: isHQDelivery ? currentUser.store_id : parseInt(selectedBranch),
        is_hq_delivery: isHQDelivery
      });
      if (res.success) {
        const info = res.data
          ? `\n${isHQDelivery ? '总店自行配送，全额收入' : `佣金：$${res.data.commission_amount}\n分店收入：$${res.data.branch_amount}`}`
          : '';
        alert(`指派成功！${info}`);
        setShowAssign(false);
        loadOrders();
      } else {
        alert(res.message || '指派失败');
      }
    } catch (err) {
      alert(err.response?.data?.message || '指派失败');
    } finally {
      setAssigning(false);
    }
  };

  const handleAction = async (action, order) => {
    // 确认退款单独走弹窗流程（需要填退款金额/方式/备注），不再是一句话确认就直接调接口
    if (action === 'confirm-refund') {
      setRefundOrder(order);
      return;
    }

    const confirmMap = {
      confirm: '确认接单？',
      prepare: '确认开始备货？备货后将扣减库存。',
      deliver: order.delivery_type === 'pickup' ? '确认商品已备好，通知用户自提？' : '确认开始配送？',
      complete: '确认订单已完成？完成后将自动结算。',
    };
    if (!window.confirm(confirmMap[action])) return;
    try {
      let res;
      if (action === 'confirm') res = await ordersManageApi.confirm(order.id);
      else if (action === 'prepare') res = await ordersManageApi.prepare(order.id);
      else if (action === 'deliver') res = await ordersManageApi.deliver(order.id);
      else if (action === 'complete') res = await ordersManageApi.complete(order.id);
      if (res.success) { alert(res.message); loadOrders(); }
      else alert(res.message || '操作失败');
    } catch (err) {
      alert(err.response?.data?.message || '操作失败');
    }
  };

  const handleConfirmRefund = async (refundData) => {
    if (!refundOrder) return;
    setRefundSubmitting(true);
    try {
      const res = await ordersManageApi.confirmRefund(refundOrder.id, refundData);
      if (res.success) {
        alert(res.message);
        setRefundOrder(null);
        loadOrders();
      } else {
        alert(res.message || '操作失败');
      }
    } catch (err) {
      alert(err.response?.data?.message || '操作失败');
    } finally {
      setRefundSubmitting(false);
    }
  };

  // 打印:先把要打印的订单数据设进state，等DOM更新完再触发浏览器打印
  const handlePrint = (order) => {
    setPrintOrder(order);
    setTimeout(() => window.print(), 100);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold">线上订单</h2>
          <p className="text-xs text-gray-600 mt-1">
            {isHQ ? '管理所有 H5 线上订单' : '处理分配给本店的订单'}
          </p>
        </div>
        <OrderFilterTabs
          tabs={filterTabs}
          filterStatus={filterStatus}
          filterExtraParams={filterExtraParams}
          onChange={(value, extraParams) => {
            setFilterStatus(value);
            setFilterExtraParams(extraParams || null);
          }}
          onRefresh={loadOrders}
        />
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">加载中...</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">暂无订单</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(order => (
            <OrderCard
              key={order.id}
              order={order}
              isExpanded={expandedId === order.id}
              isHQ={isHQ}
              isManager={isManager}
              currentStoreId={currentUser?.store_id}
              onToggle={toggleDetail}
              onAssign={openAssign}
              onAction={handleAction}
              onPrint={handlePrint}
            />
          ))}
        </div>
      )}

      {showAssign && (
        <AssignDeliveryDialog
          orderNo={assignOrderNo}
          branches={branches}
          selectedBranch={selectedBranch}
          onSelectBranch={setSelectedBranch}
          assigning={assigning}
          onConfirm={handleAssign}
          onClose={() => setShowAssign(false)}
        />
      )}

      {refundOrder && (
        <ConfirmRefundDialog
          order={refundOrder}
          submitting={refundSubmitting}
          onConfirm={handleConfirmRefund}
          onClose={() => setRefundOrder(null)}
        />
      )}

      {/* 打印专用文档：整个页面只有这一份，平时不可见 */}
      <PrintLabelDocument order={printOrder} />
    </div>
  );
};

export default OnlineOrdersPage;